/**
 * Agent Form Validation Schemas
 * 
 * Zod schemas for agent create and update forms.
 * Based on backend schema (api/src/agents/schemas.py)
 */

import { z } from 'zod';

// =============================================================================
// Model Configuration Schemas
// =============================================================================

export const modelConfigSchema = z.object({
  provider: z.enum(['azure-foundry', 'local']),
  model: z.string().min(1, 'Model is required'),
  catalog_slug: z.string().optional().nullable(),
  temperature: z.number().min(0).max(2).default(0.3),
  max_tokens: z.number().min(1).max(32000).optional(),
  deployment_name: z.string().optional().nullable(),
  api_version: z.string().optional().nullable(),
});

export const modelsConfigSchema = z.object({
  main: modelConfigSchema,
  router: modelConfigSchema.optional().nullable(),
  tools: modelConfigSchema.optional().nullable(),
  summarizer: modelConfigSchema.optional().nullable(),
  fallback: modelConfigSchema.optional().nullable(),
});

// =============================================================================
// Prompts Configuration Schemas
// =============================================================================

export const promptConfigSchema = z.object({
  system: z
    .string()
    .min(10, 'System prompt must be at least 10 characters')
    .max(10000, 'System prompt cannot exceed 10000 characters'),
});

export const promptsConfigSchema = z.object({
  main: promptConfigSchema,
  router: promptConfigSchema.optional().nullable(),
  guardrails: z.array(z.string()).optional().default([]),
});

// =============================================================================
// Knowledge Configuration Schemas
// =============================================================================

export const collectionConfigSchema = z.object({
  name: z.string().min(1),
  priority: z.number().min(1).default(1),
  filters: z.record(z.string(), z.unknown()).prefault({}),
});

export const retrievalConfigSchema = z.object({
  top_k: z.number().min(1).default(10),
  rerank_enabled: z.boolean().default(true),
  score_threshold: z.number().min(0).max(1).default(0.7),
  reference_budget_tokens: z.number().min(1).default(50000),
});

export const knowledgeConfigSchema = z.object({
  collections: z.array(collectionConfigSchema).optional().default([]),
  retrieval: retrievalConfigSchema.prefault({}),
  taxonomy_preferences: z.array(z.string()).optional().default([]),
  taxonomy_boost: z.number().min(0).max(1).default(0.3),
});

/**
 * Normalize a persisted `knowledge_config` for the editor form.
 *
 * The backend canonical write name is `retrieval.rerank_enabled`; agents saved
 * before Sprint 362 carry `retrieval.rerank`. Reading the legacy key here keeps
 * `rerank: false` from flipping to the default `true` when the form hydrates.
 * The legacy key is dropped so saving always writes the canonical name.
 */
export function normalizeKnowledgeConfigForForm(
  config: unknown,
): z.infer<typeof knowledgeConfigSchema> {
  const base = defaultAgentFormValues.knowledge_config;
  const raw = (config ?? {}) as Record<string, unknown>;
  const rawRetrieval = (raw.retrieval ?? {}) as Record<string, unknown>;

  const legacy = rawRetrieval.rerank;
  const canonical = rawRetrieval.rerank_enabled;
  const rerankEnabled =
    typeof canonical === 'boolean'
      ? canonical
      : typeof legacy === 'boolean'
        ? legacy
        : true;

  const restRetrieval = { ...rawRetrieval };
  delete restRetrieval.rerank;

  return {
    ...base,
    ...raw,
    retrieval: {
      ...base.retrieval,
      ...restRetrieval,
      rerank_enabled: rerankEnabled,
    },
  } as z.infer<typeof knowledgeConfigSchema>;
}

// =============================================================================
// Tools Configuration Schema
// =============================================================================

export const toolConfigSchema = z.object({
  server: z.string().min(1),
  operations: z.array(z.string()),
});

// =============================================================================
// Delegation Configuration Schema
// =============================================================================

export const delegationConfigSchema = z.object({
  enabled: z.boolean().default(false),
  can_be_delegated_to: z.boolean().default(false),
  max_depth: z.number().min(1).max(5).default(3),
  delegation_prompt: z.string().max(2000).optional().nullable(),
  max_response_tokens: z.number().min(256).max(16384).default(4096),
  max_fan_out: z.number().min(1).max(10).default(5),
});

export const runnerPolicyConfigSchema = z.object({
  allowed_runner_ids: z.array(z.string()).optional().default([]),
});

// =============================================================================
// Pipeline Policy Schemas (Sprint 278 — ROUTE-PIPE-1/A, REQ-LLM-006 §8.1)
// =============================================================================
// Bloques de override por agente/hive (`settings.routing|optimization|loop|
// autonomy|timeouts`). Todos los campos opcionales: ausente/null = heredar.
// Los techos visibles coinciden con los clamps del resolver y la validación
// fail-closed de la API.

export const routingPolicySchema = z.object({
  enabled: z.boolean().nullish(),
  mode: z.enum(['auto', 'direct', 'planned']).nullish(),
  on_failure: z
    .enum(['degrade_direct', 'fail_closed', 'skip_stage', 'preserve_mode'])
    .nullish(),
  confidence_threshold: z.number().min(0).max(1).nullish(),
  timeout_ms: z.number().int().min(1).max(5000).nullish(),
  budget_tokens: z.number().int().min(0).nullish(),
  planning_on_failure: z.enum(['skip', 'fail_closed']).nullish(),
  grounding_check: z.enum(['off', 'warn', 'enforce']).nullish(),
  plan_review: z.enum(['off', 'high_risk', 'always']).nullish(),
  retrieval_gating_enabled: z.boolean().nullish(),
});

export const optimizationPolicySchema = z.object({
  enabled: z.boolean().nullish(),
});

export const loopPolicySchema = z.object({
  max_iterations: z.number().int().min(1).max(10).nullish(),
});

export const autonomyPolicySchema = z.object({
  frontier: z.boolean().nullish(),
  max_duration_minutes: z.number().int().min(1).max(4320).nullish(),
  on_unavailable: z.enum(['standard', 'fail_closed']).nullish(),
});

export const timeoutPolicySchema = z.object({
  foreground_seconds: z.number().int().min(1).max(3600).nullish(),
  delegation_sync_seconds: z.number().int().min(1).max(3600).nullish(),
  delegation_background_seconds: z.number().int().min(1).max(28800).nullish(),
  fanout_aggregation_seconds: z.number().int().min(1).max(28800).nullish(),
});

// =============================================================================
// Settings Configuration Schema
// =============================================================================

export const settingsConfigSchema = z.object({
  max_context_messages: z.number().min(1).max(100).default(20),
  context_compression_enabled: z.boolean().default(true),
  feedback_enabled: z.boolean().default(true),
  feedback_required: z.boolean().default(false),
  log_conversations: z.boolean().default(true),
  log_tool_calls: z.boolean().default(true),
  delegation: delegationConfigSchema.prefault({}),
  runner_policy: runnerPolicyConfigSchema.prefault({}),
  // Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1): overrides de la política
  // del pipeline por agente (mismos bloques que settings del hive).
  routing: routingPolicySchema.nullish(),
  optimization: optimizationPolicySchema.nullish(),
  loop: loopPolicySchema.nullish(),
  autonomy: autonomyPolicySchema.nullish(),
  timeouts: timeoutPolicySchema.nullish(),
});

// =============================================================================
// Agent Form Schemas
// =============================================================================

export const agentCreateSchema = z.object({
  name: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name cannot exceed 100 characters')
    .regex(/^[\w\s\-.]+$/, 'Name can only contain letters, numbers, spaces, hyphens, and dots'),
  
  description: z
    .string()
    .max(500, 'Description cannot exceed 500 characters')
    .optional()
    .nullable()
    .default(''),
  
  icon: z.string().max(50).default('bot'),
  
  models_config: modelsConfigSchema,
  
  prompts_config: promptsConfigSchema,
  
  knowledge_config: knowledgeConfigSchema.prefault({}),
  
  tools_config: z.array(toolConfigSchema).optional().default([]),
  
  settings: settingsConfigSchema.prefault({}),
});

export const agentUpdateSchema = agentCreateSchema.partial().extend({
  status: z.enum(['draft', 'active', 'deprecated', 'archived']).optional(),
});

// =============================================================================
// Type Exports
// =============================================================================

export type ModelConfig = z.infer<typeof modelConfigSchema>;
export type ModelsConfig = z.infer<typeof modelsConfigSchema>;
export type PromptConfig = z.infer<typeof promptConfigSchema>;
export type PromptsConfig = z.infer<typeof promptsConfigSchema>;
export type CollectionConfig = z.infer<typeof collectionConfigSchema>;
export type RetrievalConfig = z.infer<typeof retrievalConfigSchema>;
export type KnowledgeConfig = z.infer<typeof knowledgeConfigSchema>;
export type ToolConfig = z.infer<typeof toolConfigSchema>;
export type SettingsConfig = z.infer<typeof settingsConfigSchema>;
export type DelegationConfig = z.infer<typeof delegationConfigSchema>;
export type RunnerPolicyConfig = z.infer<typeof runnerPolicyConfigSchema>;
export type AgentCreate = z.infer<typeof agentCreateSchema>;
export type AgentUpdate = z.infer<typeof agentUpdateSchema>;

// =============================================================================
// Default Values
// =============================================================================

export const defaultAgentFormValues: AgentCreate = {
  name: '',
  description: '',
  icon: 'bot',
  models_config: {
    main: {
      provider: 'azure-foundry',
      model: 'claude-sonnet-4-20250514',
      temperature: 0.3,
    },
    router: null,
    tools: null,
    summarizer: null,
    fallback: null,
  },
  prompts_config: {
    main: {
      system: 'You are a helpful assistant.',
    },
    router: null,
    guardrails: [],
  },
  knowledge_config: {
    collections: [],
    retrieval: {
      top_k: 10,
      rerank_enabled: true,
      score_threshold: 0.7,
      reference_budget_tokens: 50000,
    },
    taxonomy_preferences: [],
    taxonomy_boost: 0.3,
  },
  tools_config: [],
  settings: {
    max_context_messages: 20,
    context_compression_enabled: true,
    feedback_enabled: true,
    feedback_required: false,
    log_conversations: true,
    log_tool_calls: true,
    delegation: {
      enabled: false,
      can_be_delegated_to: false,
      max_depth: 3,
      delegation_prompt: null,
      max_response_tokens: 4096,
      max_fan_out: 5,
    },
    runner_policy: {
      allowed_runner_ids: [],
    },
  },
};
